/**
 * Chargement progressif du Feed : chaque source (devoirs, essais, défis…) est
 * lue page par page (pagination par curseur « date + id », la plus récente en
 * premier), puis toutes les sources sont fusionnées par date.
 *
 * Règle de fusion : tant qu'une source a encore des pages, on ne peut afficher
 * que les éléments PLUS RÉCENTS que le dernier élément chargé de cette source —
 * sinon un élément plus ancien d'une autre source s'afficherait avant un
 * élément encore non chargé de celle-ci. Cette limite est la « frontière » :
 * la plus récente des dates de curseur des sources qui ont encore des pages.
 * Pur (testé).
 */
import type { FeedItem } from '@/components/news/types';

/** Lignes lues par page et par source. */
export const FEED_PAGE_SIZE = 10;

/** Position de reprise dans une source : date de tri + id (départage). */
export type FeedCursor = { date: string; id: string };

/** Une page lue : lignes à afficher + curseur de la page suivante (null = fin). */
export type FeedPage<Row> = { rows: Row[]; next: FeedCursor | null };

export type FeedSourceState = {
  items: FeedItem[];
  /** Curseur de la prochaine page (null = source entièrement chargée). */
  next: FeedCursor | null;
};

/**
 * Filtre PostgREST « strictement après le curseur » dans l'ordre
 * `col desc, id desc` (à passer à `.or()`). Les valeurs sont entre guillemets :
 * une date contient `.` et `:`, réservés dans la syntaxe des filtres logiques.
 */
export function keysetFilter(column: string, cursor: FeedCursor): string {
  const date = `"${cursor.date}"`;
  return `${column}.lt.${date},and(${column}.eq.${date},id.lt.${cursor.id})`;
}

/** Curseur de la page suivante : null si la page n'est pas pleine. */
export function nextCursor<Row>(
  rawRows: Row[],
  pageSize: number,
  cursorOf: (row: Row) => FeedCursor,
): FeedCursor | null {
  if (rawRows.length < pageSize) return null;
  const last = rawRows[rawRows.length - 1];
  return last ? cursorOf(last) : null;
}

/** La plus récente des dates de curseur des sources qui ont encore des pages. */
export function feedFrontier(sources: FeedSourceState[]): string | null {
  let frontier: string | null = null;
  for (const s of sources) {
    if (!s.next) continue;
    if (frontier === null || s.next.date.localeCompare(frontier) > 0) frontier = s.next.date;
  }
  return frontier;
}

/**
 * Fusionne les sources : tous les éléments chargés, du plus récent au plus
 * ancien, sans ceux qui dépassent la frontière (ils s'afficheront quand les
 * sources en retard auront chargé leur page suivante).
 */
export function mergeFeedSources(sources: FeedSourceState[]): FeedItem[] {
  const frontier = feedFrontier(sources);
  const seen = new Set<string>();
  const visible: FeedItem[] = [];
  for (const item of sources.flatMap((s) => s.items)) {
    if (frontier !== null && item.date.localeCompare(frontier) < 0) continue;
    // Un élément ne s'affiche qu'une fois, même relu par deux pages.
    const key = `${item.kind}:${item.data.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    visible.push(item);
  }
  return visible.sort((a, b) => b.date.localeCompare(a.date));
}

/**
 * Sources à charger pour faire avancer le fil : celles qui fixent la frontière
 * (leur curseur est le plus récent). Renvoie leurs indices.
 */
export function sourcesToAdvance(sources: FeedSourceState[]): number[] {
  const frontier = feedFrontier(sources);
  if (frontier === null) return [];
  return sources.flatMap((s, i) => (s.next && s.next.date === frontier ? [i] : []));
}
